<template>
    <div>
        <b-sidebar
            v-model="filtering.isSidebarVisible"
            :title="`${getAttributionName(filtering.selectedFilter)}`"
            width="400px"
            left
            bg-variant="white"
            backdrop
            shadow
            body-class="cost-pivot-sidebar-body"
        >
            <div class="sticky-top mb-25" :class="{'bg-white': skin == 'light'}">
                <div class="d-flex justify-content-between px-1 mb-50">
                    <b-link
                        class="mt-25 mr-75 pt-50 px-0"
                        variant="outline-secondary"
                        @click="selectAllFilterValues()"
                    >
                        <i class="fas fa-lg"
                            :class="{
                                'fa-check-square': filtering.valuesOfCurrentFilter.length == filtering.aboveValues.length + filtering.belowValues.length,
                                'fa-square': filtering.valuesOfCurrentFilter.length != filtering.aboveValues.length + filtering.belowValues.length
                            }"
                        ></i>
                    </b-link>
                    <b-input-group class="flex-grow-1 mr-25">
                        <b-input
                            :placeholder="$t('v4.filter')"
                            v-model="filtering.filterStringTemp"
                            @keydown.enter="applyFilterForFiltering"
                        />
                        <b-input-group-append>
                            <b-button
                                class="px-1"
                                size="sm"
                                variant="secondary"
                                @click="applyFilterForFiltering"
                            >
                                <i class="fas fa-search"></i>
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>
                    <b-button
                        size="sm"
                        variant="primary"
                        @click="applyFilterValues()"
                        style="min-width:113px;"
                    >
                        <span class="pt-25">{{ $t('org.dashboard.Apply') }}</span>
                        <b-badge
                            variant="light"
                            class="text-dark ml-50"

                        >{{ filtering.valuesOfCurrentFilter.length > 100 ? '100+' : filtering.valuesOfCurrentFilter.length }}</b-badge>
                    </b-button>
                </div>
            </div>
            <RecycleScroller
                ref="scrollerRef"
                class="cost-pivot-filter-scroller px-0 pb-2"
                :items="filtering.mergedFilterValues"
                :item-size="32"
                :buffer="1000"
                key-field="key"
                type-field="type"
            >
                <template #before>
                    <b-alert
                        v-if="filtering.uniqValues.length < 1"
                        show
                        class="p-2 mb-0 mx-1"
                        variant="danger"
                    >
                        <template v-if="costPivotStatusCode === 204">
                            {{ $t('v4.No usage data for this account/period') }}
                        </template>
                        <template v-else-if="filtering.belowValues.length > 0">
                            {{ $t('v4.No values match current filter') }}
                            <template v-if="filtering.belowValues.some(v => filtering.valuesOfCurrentFilter.includes(v))">
                                {{ $t('v4.Uncheck italicized below to see other values') }}
                            </template>
                        </template>
                        <template v-else>
                            {{ $t('v4.There are no available values of X', { X: getAttributionName(filtering.selectedFilter) }) }}
                        </template>
                    </b-alert>
                </template>
                <template v-slot="{ item }">
                    <div v-if="item.type === 'separator'" class="cost-pivot-filter-separator">
                        <hr class="m-0" />
                    </div>
                    <p
                        v-else
                        class="d-flex m-0 px-1 py-25 cost-pivot-filter-row"
                        :class="[
                            item.type === 'italic' ? 'font-italic' : 'font-weight-bold',
                            { 'bg-warning': filtering.filterString !== '' && item.val.toString().toLowerCase().includes(filtering.filterString) }
                        ]"
                    >
                        <b-checkbox
                            :value="item.val"
                            v-model="filtering.valuesOfCurrentFilter"
                            class="cost-pivot-filter-checkbox"
                        >
                            <span
                                :class="{ 'text-danger': item.val === 'N/A' }"
                                :title="item.val"
                            >{{ item.val }}</span>
                        </b-checkbox>
                    </p>
                </template>
            </RecycleScroller>
        </b-sidebar>
        <div class="mb-0">
            <b-overlay
                :show="loading"
                rounded="sm"
            >
                <template #overlay v-if="loading">
                    <div class="text-center">
                        <p class="mb-50" v-if="currentDType === 'file'">
                            {{ $t('org.billing.Creating a CSV file') }}
                        </p>
                        <p class="mb-50" v-else-if="currentDType === 'email'">
                            {{ $t('org.billing.You have requested to send an email Please check back later') }}
                        </p>
                        <b-spinner />
                    </div>
                </template>
                <b-table-simple
                    fixed
                    outlined
                    responsive
                    class="mb-0"
                >
                    <colgroup>
                        <col style="width:200px;" />
                        <col />
                    </colgroup>
                    <b-thead :class="{ 'cost-pivot-tools-disabled': noData }">
                        <b-tr>
                            <b-td
                                class="p-50 align-top"
                            >
                                <b-select
                                    v-model="rendering.selected"
                                    :options="rendering.rendererNameOptions"
                                    :disabled="downloadMode || noData"
                                    size="sm"
                                ></b-select>
                            </b-td>
                            <b-td
                                class="pt-50 pl-50 pr-0 pb-25 border-left"
                            >
                                <draggable :list="defaultAttributes.filter(attr => !rows.includes(attr) && !cols.includes(attr))" group="sources" draggable=".item" :disabled="noData" @change="checkRemove">
                                    <b-button
                                        v-for="attr in defaultAttributes.filter(attr => !rows.includes(attr) && !cols.includes(attr))"
                                        :key="attr"
                                        class="item mt-0 mr-25 mb-25 ml-0 pt-50"
                                        :class="{'pl-75': ['ymd', 'ym', 'd'].includes(attr)}"
                                        size="sm"
                                        variant="outline-secondary"
                                    >
                                        <i v-if="['ymd', 'ym', 'w', 'd'].includes(attr)" class="fas fa-calendar mr-25"></i>
                                        {{ getAttributionName(attr).replace('TAG:', '') }}
                                    </b-button>
                                </draggable>
                                <slot name="dropdown-description"></slot>
                                <draggable v-if="descriptionItemsAttributes.length" :list="descriptionItemsAttributes.filter(attr => !rows.includes(attr) && !cols.includes(attr))" group="sources" draggable=".item" :disabled="noData" @change="checkRemove">
                                    <b-button
                                        v-for="attr in descriptionItemsAttributes.filter(attr => !rows.includes(attr) && !cols.includes(attr))"
                                        :key="attr"
                                        class="item mt-0 mr-25 mb-25 ml-0 pt-50"
                                        size="sm"
                                        variant="outline-secondary"
                                    >
                                        {{ getAttributionName(attr).replace('TAG:', '') }}
                                    </b-button>
                                </draggable>
                                <slot name="dropdown-resource"></slot>
                                <draggable v-if="resourceTagsAttributes.length" :list="resourceTagsAttributes.filter(attr => !rows.includes(attr) && !cols.includes(attr))" group="sources" draggable=".item" :disabled="noData" @change="checkRemove">
                                    <b-button
                                        v-for="attr in resourceTagsAttributes.filter(attr => !rows.includes(attr) && !cols.includes(attr))"
                                        :key="attr"
                                        class="item mt-0 mr-25 mb-25 ml-0 pt-50"
                                        :class="{'pl-75': attr.includes('TAG:')}"
                                        size="sm"
                                        variant="outline-secondary"
                                    >
                                        <i v-if="attr.includes('TAG:')" class="fas fa-tag mr-25"></i>
                                        {{ getAttributionName(attr).replace('TAG:', '') }}
                                    </b-button>
                                </draggable>
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-td
                                class="p-50 align-bottom"
                            >
                                <b-select
                                    v-model="outputFormatting.selected"
                                    :options="outputFormatting.pivotValueOptions"
                                    size="sm"
                                    :disabled="noData"
                                ></b-select>
                            </b-td>
                            <b-td
                                class="pt-50 pl-50 pr-0 pb-25 border-left"
                            >
                                <draggable :list="config.cols" group="sources" draggable=".item" @change="checkRemove" :disabled="noData">
                                    <b-button-group
                                        class="item mt-0 mb-25 ml-0 mr-25"
                                        v-for="item in config.cols"
                                        :key="item"
                                    >
                                        <b-button
                                            v-if="!downloadMode"
                                            class="pt-50"
                                            :class="{'pr-50': getAttributionFilterValueLength(item) > -1, 'pl-75': item.includes('TAG:') || ['ymd', 'ym', 'd'].includes(item)}"
                                            size="sm"
                                            :variant="filtering.itemNamesOfHavingFilterValues.includes(item) ? 'primary' : 'outline-primary'"
                                            :disabled="noData"
                                            @click="openFilterModal(item)"
                                        >
                                            <i v-if="item.includes('TAG:')" class="fas fa-tag mr-25"></i>
                                            <i v-else-if="['ymd', 'ym', 'd'].includes(item)" class="fas fa-calendar mr-25"></i>
                                            {{ getAttributionName(item).replace('TAG:', '') }}
                                        </b-button>
                                        <span
                                            v-else
                                            class="btn btn-sm btn-outline-primary pt-50"
                                            :class="{'pl-75': item.includes('TAG:') || ['ymd', 'ym', 'd'].includes(item)}"
                                        >
                                            <i v-if="item.includes('TAG:')" class="fas fa-tag mr-25"></i>
                                            <i v-else-if="['ymd', 'ym', 'd'].includes(item)" class="fas fa-calendar mr-25"></i>
                                            {{ getAttributionName(item).replace('TAG:', '') }}
                                        </span>
                                        <b-button
                                            v-if="!downloadMode && getAttributionFilterValueLength(item) > 0"
                                            class="pt-50 px-50"
                                            variant="outline-primary"
                                            size="sm"
                                            :disabled="noData"
                                        >
                                            {{ getAttributionFilterValueLength(item) }}
                                        </b-button>
                                    </b-button-group>
                                </draggable>
                            </b-td>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr>
                            <b-td
                                class="pt-50 pl-50 pr-25 pb-25 align-top d-flex align-content-stretch"
                                :class="{ 'cost-pivot-tools-disabled': noData }"
                            >
                                <draggable
                                    :list="config.rows"
                                    group="sources"
                                    draggable=".item"
                                    @change="checkRemove"
                                    class="d-flex align-items-start flex-column flex-fill"
                                    style="padding-bottom:60%;"
                                    :disabled="noData"
                                >
                                    <b-button-group
                                        class="item mt-0 mb-25 ml-0 mr-25"
                                        v-for="item in config.rows"
                                        :key="item"
                                    >
                                        <b-button
                                            v-if="!downloadMode"
                                            class="pt-50"
                                            :class="{'pr-50': getAttributionFilterValueLength(item) > -1, 'pl-75': item.includes('TAG:') || ['ymd', 'ym', 'd'].includes(item)}"
                                            size="sm"
                                            :variant="filtering.itemNamesOfHavingFilterValues.includes(item) ? 'primary' : 'outline-primary'"
                                            :disabled="noData"
                                            @click="openFilterModal(item)"
                                        >
                                            <i v-if="item.includes('TAG:')" class="fas fa-tag mr-25"></i>
                                            <i v-else-if="['ymd', 'ym', 'd'].includes(item)" class="fas fa-calendar mr-25"></i>
                                            {{ getAttributionName(item).replace('TAG:', '') }}
                                            <i
                                                v-if="getAttributionFilterValueLength(item) == 0"
                                                class="fas fa-caret-right fa-sm ml-25"
                                            />
                                        </b-button>
                                        <span
                                            v-else
                                            class="btn btn-sm btn-outline-primary pt-50"
                                            :class="{'pl-75': item.includes('TAG:') || ['ymd', 'ym', 'd'].includes(item)}"
                                        >
                                            <i v-if="item.includes('TAG:')" class="fas fa-tag mr-25"></i>
                                            <i v-else-if="['ymd', 'ym', 'd'].includes(item)" class="fas fa-calendar mr-25"></i>
                                            {{ getAttributionName(item).replace('TAG:', '') }}
                                        </span>
                                        <b-button
                                            v-if="!downloadMode && getAttributionFilterValueLength(item) > 0"
                                            class="pt-50 px-50"
                                            variant="outline-primary"
                                            size="sm"
                                            :disabled="noData"
                                        >
                                            {{ getAttributionFilterValueLength(item) }}
                                        </b-button>
                                    </b-button-group>
                                </draggable>
                            </b-td>
                            <b-td class="p-0 align-top">
                                <!-- 로딩이 아니고 서버 메시지 있는 경우 -->
                                <!-- 내부 사용자이거나 고객인데 응답코드가 406이 아닌 경우 (e.g 429) -->
                                <div v-if="serverMessage && ((internalUser) || (!internalUser && statusCode !== 205))">
                                    <b-alert
                                        show
                                        class="text-center p-5 m-50"
                                        variant="danger"
                                    >
                                        <feather-icon icon="AlertTriangleIcon" />
                                        {{ serverMessage }}
                                    </b-alert>
                                </div>
                                <!-- 대용량 파일 다운로드가 완료되서 S3 링크가 제공된 경우 -->
                                <div v-else-if="(currentDType === 'file' || downloadMode) && downloadStatus === 'DONE'">
                                    <b-alert
                                        v-if="downloadLink"
                                        variant="secondary"
                                        class="text-center p-5 m-50"
                                        show
                                    >
                                        <b-button-group class="mb-50">
                                            <b-button
                                                variant="primary"
                                                @click="handleClickDownload"
                                            >
                                                <feather-icon icon="DownloadIcon" />
                                                {{ $t('v4.Download CSV') }}
                                            </b-button>
                                            <b-link
                                                v-if="locale != 'en'"
                                                class="btn btn-outline-secondary"
                                                target="_new"
                                                :href="`/user-guide?path=docs%2F${namespace}%2F${locale}%2Fcsv-encoding%2Fdocument.json`"
                                            >
                                                <feather-icon icon="FileTextIcon" />
                                                {{ $t('v4.Encoding Conversion Guide') }}
                                            </b-link>
                                        </b-button-group>

                                        <p
                                            class="mb-25 text-center small"
                                        >
                                            ※ {{ $t('org.billing.CSV file contains Korean characters will not be displayed correctly in the Excel program') }}
                                        </p>
                                        <p
                                            v-if="locale != 'en'"
                                            class="mb-0 text-center small"
                                        >
                                            {{  $t('org.billing.Please refer to the encoding conversion guide') }}
                                        </p>
                                    </b-alert>
                                </div>
                                <!-- 이메일 전송 완료 -->
                                <div v-else-if="currentDType === 'email' && downloadStatus === 'DONE'">
                                    <b-alert
                                        variant="secondary"
                                        class="text-center p-5 m-50"
                                        show
                                    >
                                        <p class="m-0">
                                            {{ $t('org.billing.You have requested to send an email Please check back later') }}
                                        </p>
                                    </b-alert>
                                </div>
                                <!-- 다운로드 모드에서 statusCode가 있거나 일반모드에서 205인 경우 -->
                                <div v-else-if="!loading && ((downloadMode && statusCode) || (!downloadMode && statusCode === 205)) && downloadStatus !== 'DONE'"
                                     :style="downloadMode ? 'height: 100%; display: flex; align-items: center;' : ''">
                                    <b-alert
                                        variant="secondary"
                                        class="text-center p-5 m-50 w-100"
                                        :style="downloadMode ? 'min-height: 300px; display: flex; flex-direction: column; justify-content: center;' : ''"
                                        show
                                    >
                                        <!-- 다운로드 모드에서 데이터 없음(204) -->
                                        <template v-if="downloadMode && statusCode === 204">
                                            <p class="mb-0 text-center">{{ $t('org.message.There are no usage details') }}</p>
                                        </template>
                                        <!-- 다운로드 모드에서 데이터 있음 또는 일반 모드 -->
                                        <template v-else>
                                            <div v-if="downloadMode" class="mb-50 text-center">
                                                <p class="mb-25">{{ $t('v4["Download Mode is enabled."]') }}</p>
                                                <p class="mb-0">{{ $t('v4["Request CSV download"]') }}</p>
                                            </div>
                                            <p v-else class="mb-50 text-center">{{ $t('org.billing.Too many rows retrieved to display on the screen') }}</p>
                                            <div class="d-flex justify-content-center mb-50">
                                                <b-button-group>
                                                    <b-button variant="primary" @click="largeFileDownload('file')">
                                                        <feather-icon icon="FileIcon" />
                                                        {{ $t('v4.CSV Download Request') }}
                                                    </b-button>
                                                <b-button v-if="!downloadMode" variant="warning" @click="largeFileDownload('email')">
                                                    <feather-icon icon="MailIcon" />
                                                    {{ $t('v4.Send Email') }}
                                                </b-button>
                                                </b-button-group>
                                            </div>
                                            <p class="mb-50 text-center small" v-if="!downloadMode">
                                                ※ {{ $t('org.billing.download the file from your browser or receive a link via email') }}
                                            </p>
                                        </template>
                                    </b-alert>
                                </div>

                                <!-- 로딩이 아니고 서버 메시지 없고 사이즈가 많은 경우 -->
                                <div v-else-if="!viewTable && size > ROW_MAX_LIMIT">
                                    <b-alert
                                        variant="secondary"
                                        class="text-center p-5 m-50"
                                        show
                                    >
                                        <div class="alert-body">
                                            <p class="mb-50">{{ $tc('org.billing.Too much data to display (n) Please download CSV file', size) }}</p>
                                            <p class="mb-50">{{ $t('org.billing.Do you want to display on this page or download CSV file?')}}</p>
                                            <b-button-group>
                                                <b-button variant="primary" @click="csvDownload(pivotData)">
                                                    <feather-icon icon="DownloadIcon" />
                                                    {{ $t('v4.CSV Download') }}
                                                </b-button>
                                                <b-button variant="warning" @click="pivotViewTable">
                                                    <feather-icon icon="EyeIcon" />
                                                    {{ $t('v4.View table') }}
                                                </b-button>
                                            </b-button-group>
                                        </div>
                                    </b-alert>
                                </div>
                                <div
                                    v-else-if="!downloadMode && data.length < 1"
                                >
                                    <!-- 데이터가 없는 경우 -->
                                    <b-alert
                                        variant="warning"
                                        class="text-center p-5 m-50"
                                        show
                                    >

                                        <div class="alert-body">
                                            {{ $t('org.message.There are no usage details') }}
                                        </div>
                                    </b-alert>
                                </div>
                                <!-- 기본 TABLE UI 노출 -->
                                <VuePivottable
                                    v-else
                                    v-model="config"
                                    :data="data"
                                    :aggregators="aggregators"
                                    :aggregatorName="aggregatorName"
                                    :rendererName="rendering.selected"
                                    :rows="rows"
                                    :cols="cols"
                                    :vals="vals"
                                />
                            </b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
            </b-overlay>
        </div>
    </div>
</template>

<script>
import useCostPivot from '@/composables/useCostPivot'
import useAppConfig from '@core/app-config/useAppConfig'
import { VuePivottable, PivotUtilities, Renderer } from 'vue-pivottable'
import { getCurrentInstance, computed, ref, toRefs, watch, reactive, nextTick } from '@vue/composition-api'
import draggable from 'vuedraggable'
import { RecycleScroller } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import bus from '@/libs/bus'
export default {
    components: {
        VuePivottable,
        draggable,
        RecycleScroller
    },
    model: {
        prop: 'items',
        event: 'sync:items'
    },
    data () {
        return {
            ROW_MAX_LIMIT: 10000
        }
    },
    props: {
        items: {
            type: Object,
            default: function () {
                return {
                    rows: ['usageAccountId', 'unblendedrate'],
                    cols: ['ym'],
                    compositSources: ['usageAccountId', 'ym'],
                    value: 'unblendedCost'
                }
            }
        },
        data: {
            type: Array,
            default: function () {
                return []
            }
        },
        pivotFilter: {
            type: Object,
            default: function () {
                return {}
            }
        },
        downloadStatus: {
            type: String,
            default: ''
        },
        downloadLink: {
            type: String,
            default: ''
        },
        internalUser: {
            type: Boolean,
            default: false
        },
        serverMessage: {
            type: String,
            default: null
        },
        statusCode: {
            type: [Number, String],
            default: 200
        },
        loading: {
            type: Boolean,
            default: false
        },
        fieldStatus: {
            type: Object,
            default: function () {
                return {
                    description: false,
                    resources: false
                }
            }
        },
        size: {
            type: Number,
            default: 0
        },
        accountMap: {
            type: Object,
            default: function () {
                return {}
            }
        },
        downloadMode: {
            type: Boolean,
            default: false
        },
        noData: {
            type: Boolean,
            default: false
        },
        // 글로벌 계정/그룹 셀렉트 컨텍스트 토큰. 부모(store.getters.changeProject)에서
        // 내려온다. 값이 바뀌면 그 시점 dataValues 를 crossContextValues 로 이동.
        // pivotFilter.usageAccountId/group 은 사이드바 drill-down 필터용이라 컨텍스트
        // 신호가 아니다 — 별도 prop 로 분리.
        changeProject: {
            type: [Object, String, Number],
            default: null
        }
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        // const costPivotValue = ref('unblendedCost')
        const { data, items, pivotFilter, fieldStatus, accountMap, downloadLink, downloadMode } = toRefs(props)
        const compositSources = computed(() => items.value.compositSources)
        const aggregatorName = 'Sum'
        // const rendererNames = ['Table', 'Table Heatmap', 'Export Table TSV']
        const { attributeDefaultItems, attributeDescriptionItems, attributeResourceItems, locales, pivotValues } = useCostPivot()
        const attributeItems = computed(() => attributeDefaultItems.value.concat(attributeDescriptionItems.value).concat(attributeResourceItems.value))
        const rendererName = ref('Table')
        const currentDType = ref(null)
        const costPivotStatusCode = computed(() => vm.$store.getters['$_costPivot/costPivotStatus']?.data)
        const { skin } = useAppConfig()
        const config = ref({
            cols: ['ym'],
            rows: ['usageAccountId'],
            vals: ['value'],
            valueFilter: {}
        })

        const aggregators = computed(() => {
            const usFmt = PivotUtilities.numberFormat({ digitsAfterDecimal: 3 })
            return ((tpl) => ({
                Sum: tpl.sum(usFmt)
            })
            )(PivotUtilities.aggregatorTemplates)
        })
        const renderers = computed(() => {
            const TableRenderer = Renderer.TableRenderer
            return (() => ({
                'Table': TableRenderer.Table,
                'Table Heatmap': TableRenderer['Table Heatmap'],
                // 'Table Col Heatmap': TableRenderer['Table Col Heatmap'],
                // 'Table Row Heatmap': TableRenderer['Table Row Heatmap'],
                'Export Table TSV': TableRenderer['Export Table TSV']
            })
            )()
        })
        const savedFilter = ref({})
        const value = computed(() => items.value.vals)
        // const rows = ref(['usageAccountId'])
        // const cols = ref(['ym'])
        const cols = computed(() => config.value.cols)
        const rows = computed(() => config.value.rows)
        const vals = ref(['value'])
        const hiddenAttributes = ['value']
        const viewTable = ref(false)
        const disabledFromDragDrop = ref([])
        ///
        // temp
        const checkRemove = evt => {
            if (evt.removed) {
                if (!items.value.cols.includes(evt.removed.element) && !items.value.rows.includes(evt.removed.element)) {
                    delete filtering.dataValues[evt.removed.element]
                    delete filtering.filterAccumValues[evt.removed.element]
                    delete filtering.crossContextValues[evt.removed.element]
                    context.emit('update:pivotFilter', [...filtering.appliedFilters.filter(item => item.key !== evt.removed.element)].reduce((acc, item) => {
                        return { ...acc, [item.key]: item.values }
                    }, {}))
                }
            }
        }

        const rendering = reactive({
            selected: 'Table',
            rendererNames: ['Table', 'Table Heatmap', 'Export Table TSV'],
            rendererNameOptions: computed(() => rendering.rendererNames.map(item => ({
                value: item,
                text: vm.$t(`v4.${item}`)
            })))
        })

        const outputFormatting = reactive({
            selected: 'unblendedCost',
            pivotValueOptions: computed(() => pivotValues.value.map(item => ({
                value: item.value,
                text: item.name
            })))
        })

        const getAttributionName = key => {
            return attributeMap.value[key] ? attributeMap.value[key].name : key
        }

        const getAttributionFilterValueLength = key => {
            if (filtering.appliedFilters.find(item => item.key === key) && filtering.appliedFilters.find(item => item.key === key).values.length) {
                return filtering.appliedFilters.find(item => item.key === key).values.length
            } else if (!attributeMap.value[key] || !attributeMap.value[key].removeFilter) {
                return 0
            } else {
                return -1
            }
        }

        const scrollerRef = ref(null)

        const filtering = reactive({
            isSidebarVisible: false,
            selectedFilter: null,
            // 현재 응답 data 의 그 차원 값 — sidebar 의 b-alert 분기 / 검색 정렬 기준
            uniqValues: computed(() => filtering.selectedFilter
                ? data.value
                    .map(item => item[filtering.selectedFilter])
                    .map(val => val || (isNAAllowedKey(filtering.selectedFilter) ? 'N/A' : null))
                    .filter(val => val)
                    .filter((obj, pos, arr) => (arr.indexOf(obj) === pos))
                : []
            ),
            filterString: '',
            filterStringTemp: '',
            valuesOfCurrentFilter: [],
            itemNamesOfHavingFilterValues: computed(() => [...items.value.cols, ...items.value.rows].filter(a => data.value.map(item => Object.keys(item)).some(b => b.includes(a)))),
            appliedFilters: computed(() => Object.keys(pivotFilter.value).map(key => ({ key, values: pivotFilter.value[key] }))),
            // 세 풀 분리 (서로 독립 누적):
            //   dataValues         — 현재 컨텍스트(어카운트+그룹)에서 응답으로 받은 값. add-only, 컨텍스트 전환 시 reset
            //   filterAccumValues  — pivotFilter 로 적용된 모든 값 (HB-72 의미 그대로, 건드리지 않음)
            //   crossContextValues — 이전 컨텍스트에서 누적되었던 dataValues. 컨텍스트 전환 시 그 시점 dataValues 가 여기로 이동.
            //                        같은 컨텍스트에서 새 응답으로 그 값이 다시 들어오면 dedup 으로 제거.
            // 새로고침(컴포넌트 재마운트)이 전체 리셋 트리거.
            //
            // 분류 규칙:
            //   below (italic)  = (filterAccumValues - uniqValues - dataValues) ∪ (crossContextValues - dataValues)
            //   above (regular) = (dataValues ∪ filterAccumValues) - belowValues
            // dataValues 에 한번 누적되면 컨텍스트 전환 전까지 항상 top — 응답 좁아져도
            // italic 으로 안 떨어진다 (stale 판정에 d 멤버십 검사 추가).
            dataValues: {},
            filterAccumValues: {},
            crossContextValues: {},
            belowValues: computed(() => {
                const u = filtering.uniqValues
                const d = filtering.dataValues[filtering.selectedFilter] ?? []
                const f = filtering.filterAccumValues[filtering.selectedFilter] ?? []
                const c = filtering.crossContextValues[filtering.selectedFilter] ?? []
                const stale = f.filter(v => !u.includes(v) && !d.includes(v))
                const crossStale = c.filter(v => !d.includes(v))
                const merged = [...new Set([...stale, ...crossStale])]
                return filtering.filterString === ''
                    ? merged.sort((a, b) => a > b ? 1 : -1)
                    : merged.sort(a => a.toString().toLowerCase().includes(filtering.filterString) ? -1 : 1)
            }),
            aboveValues: computed(() => {
                const d = filtering.dataValues[filtering.selectedFilter] ?? []
                const f = filtering.filterAccumValues[filtering.selectedFilter] ?? []
                const below = filtering.belowValues
                const merged = [...new Set([...d, ...f])].filter(v => !below.includes(v))
                return filtering.filterString === ''
                    ? merged.sort((a, b) => a > b ? 1 : -1)
                    : merged.sort(a => a.toString().toLowerCase().includes(filtering.filterString) ? -1 : 1)
            }),
            // RecycleScroller 입력. type 필드로 regular / separator / italic 세 view pool 분리.
            // key 는 별도 필드 — 사용자 데이터(val) 와 separator sentinel 충돌 회피.
            mergedFilterValues: computed(() => {
                const regular = filtering.aboveValues.map(val => ({ key: 'r:' + val, val, type: 'regular' }))
                const italic = filtering.belowValues.map(val => ({ key: 'i:' + val, val, type: 'italic' }))
                if (italic.length === 0) return regular
                return [
                    ...regular,
                    { key: '__cost_pivot_filter_separator__', val: '', type: 'separator' },
                    ...italic
                ]
            })
        })

        const applyFilterForFiltering = () => {
            filtering.filterString = filtering.filterStringTemp.toLowerCase()
            // 검색 적용 → 매치 항목이 list 최상단으로 정렬됨. 사용자가 매치를 즉시 보도록 스크롤 0 으로 리셋.
            nextTick(() => {
                if (scrollerRef.value) {
                    scrollerRef.value.scrollToItem(0)
                }
            })
        }

        const openFilterModal = key => {
            if (getAttributionFilterValueLength(key) < 0) {
                bus.$emit('request:warning', { title: 'Info', content: vm.$t('org.billing.Filtering is not supported for selected item') })
                return
            }
            // aboveValues 가 0 개면 사이드바 내부의 빈 값 알림(<b-alert>) 으로 상황 표시
            filtering.filterString = ''
            filtering.filterStringTemp = ''
            filtering.selectedFilter = key
            filtering.valuesOfCurrentFilter = filtering.appliedFilters.find(item => item.key === key) ? filtering.appliedFilters.find(item => item.key === key).values : []

            setTimeout(() => {
                filtering.isSidebarVisible = true
            }, 300)
        }

        const selectAllFilterValues = () => {
            if (filtering.valuesOfCurrentFilter.length === filtering.aboveValues.length + filtering.belowValues.length) {
                filtering.valuesOfCurrentFilter = []
            } else {
                filtering.valuesOfCurrentFilter = [...filtering.aboveValues, ...filtering.belowValues]
            }
        }

        // 컨텍스트 전환(상단 글로벌 셀렉트박스 변경 = changeProject) 시 모든 풀에서 리셋되어야 하는
        // 차원. 사이드바 필터 변경 시는 일반 차원과 동일하게 누적된다 — 두 경로를 분리한다.
        const ALWAYS_LATEST_KEYS = ['usageAccountId', 'group']

        const N_A_ALLOWED_KEYS = ['resourceId', 'reservedInstancesId', 'savingsPlan_savingsPlanArn', 'operation']
        const isNAAllowedKey = key =>
            typeof key === 'string' && (N_A_ALLOWED_KEYS.includes(key) || key.includes('TAG:'))

        // 누적 풀의 scope = "현재 어카운트 + 그룹 컨텍스트". 글로벌 셀렉트박스 토큰이 바뀌면
        // 그 시점의 dataValues 를 crossContextValues 로 이동(bottom 풀로 보존) 후 dataValues 비움.
        // 이후 새 응답을 dataValues 에 누적. crossContextValues 와 dataValues 는 별개 풀.
        // 토큰 source 는 props.changeProject (부모 store getter 미러). pivotFilter 가 아닌 이유:
        // 부모는 계정/그룹 변경 시 pivotFilter.usageAccountId/group 을 비우기만 하고 새 ID 를
        // 박지 않아, pivotFilter 만 보면 컨텍스트 전환 신호를 영원히 놓친다.
        let previousContextKey = null
        const computeContextKey = () => JSON.stringify(props.changeProject ?? null)

        // data 응답 갱신 시 호출. 모든 차원은 dataValues 에 add-only push.
        // 컨텍스트 전환 시점에만 ALWAYS_LATEST_KEYS 의 세 풀을 단일 게이트로 클리어.
        // 현재 rows/cols 에 없는 키의 누적은 세 풀에서 정리 (그 차원이 더 이상 활성이 아니므로).
        const accumulateFromData = () => {
            const validKeys = [...items.value.cols, ...items.value.rows]
            validKeys.forEach(item => {
                if (!filtering.dataValues[item]) {
                    filtering.dataValues[item] = []
                }
                if (!filtering.filterAccumValues[item]) {
                    filtering.filterAccumValues[item] = []
                }
                if (!filtering.crossContextValues[item]) {
                    filtering.crossContextValues[item] = []
                }
            })
            Object.keys(filtering.dataValues).forEach(key => {
                if (!validKeys.includes(key)) {
                    delete filtering.dataValues[key]
                }
            })
            Object.keys(filtering.filterAccumValues).forEach(key => {
                if (!validKeys.includes(key)) {
                    delete filtering.filterAccumValues[key]
                }
            })
            Object.keys(filtering.crossContextValues).forEach(key => {
                if (!validKeys.includes(key)) {
                    delete filtering.crossContextValues[key]
                }
            })
            // 컨텍스트 전환 감지 — 새 응답을 dataValues 에 누적하기 전에 직전 dataValues 를 crossContextValues 로 이동.
            // (어카운트/그룹 변경 시 pivotFilter 와 data 가 같은 tick 에 갱신되어도 단일 함수 내 직렬 실행이라 race 없음.)
            const currentContextKey = computeContextKey()
            if (previousContextKey !== null && previousContextKey !== currentContextKey) {
                Object.keys(filtering.dataValues).forEach(key => {
                    if (ALWAYS_LATEST_KEYS.includes(key)) {
                        // 컨텍스트 전환 시 글로벌 차원은 모든 풀 클리어 (이전 어카운트/그룹 흔적이 italic-below 로 새지 않도록)
                        filtering.dataValues[key] = []
                        if (filtering.filterAccumValues[key]) filtering.filterAccumValues[key] = []
                        if (filtering.crossContextValues[key]) filtering.crossContextValues[key] = []
                        return
                    }
                    const prevData = filtering.dataValues[key] ?? []
                    if (!filtering.crossContextValues[key]) {
                        filtering.crossContextValues[key] = []
                    }
                    prevData.forEach(val => {
                        if (!filtering.crossContextValues[key].includes(val)) {
                            filtering.crossContextValues[key].push(val)
                        }
                    })
                    filtering.dataValues[key] = []
                })
            }
            previousContextKey = currentContextKey
            data.value.forEach(item => {
                Object.keys(item).forEach(key => {
                    if (key === 'value' || !filtering.dataValues[key]) return
                    const normalized = item[key] || (isNAAllowedKey(key) ? 'N/A' : null)
                    if (normalized && !filtering.dataValues[key].includes(normalized)) {
                        filtering.dataValues[key].push(normalized)
                    }
                })
            })
            // dedup: 새 응답으로 dataValues 에 들어온 값은 crossContextValues 에서 제거 — top 우선
            Object.keys(filtering.crossContextValues).forEach(key => {
                if (filtering.dataValues[key] && filtering.dataValues[key].length > 0) {
                    filtering.crossContextValues[key] = filtering.crossContextValues[key]
                        .filter(v => !filtering.dataValues[key].includes(v))
                }
            })
        }

        const applyFilterValues = () => {
            context.emit('update:pivotFilter', [...filtering.appliedFilters.filter(item => item.key !== filtering.selectedFilter), {
                key: filtering.selectedFilter,
                values: filtering.valuesOfCurrentFilter
            }].reduce((acc, item) => {
                return { ...acc, [item.key]: item.values }
            }, {}))

            context.emit('reload:pivotFilter')

            filtering.isSidebarVisible = false

            setTimeout(() => {
                filtering.selectedFilter = null
                filtering.valuesOfCurrentFilter = []
            }, 300)
        }

        const defaultAttributes = computed(() => {
            return attributeItems.value.filter(item => {
                return item.type === 'default'
            }).map(item => item.key)
        })
        const descriptionItemsAttributes = computed(() => {
            return attributeItems.value.filter(item => {
                return fieldStatus.value[item.type] && item.type === 'description'
            }).map(item => item.key)
        })
        const resourceTagsAttributes = computed(() => {
            return attributeItems.value.filter(item => {
                return fieldStatus.value[item.type] && item.type === 'resource'
            }).map(item => item.key)
        })
        const attributeOfDescriptionType = computed(() => attributeItems.value.filter(item => item.type === 'description').map(item => item.key))
        const attributeOfResourceType = computed(() => attributeItems.value.filter(item => item.type === 'resource').map(item => item.key))
        const setFilter = (filter) => {
            context.emit('sync:filter', filter)
        }

        const updateValueFilter = (valueFilter) => {
            if (Object.keys(valueFilter).length) {
                const removeFilterKeys = Object.keys(valueFilter)
                    .filter(key =>
                        items.value.cols &&
                        items.value.rows &&
                        !items.value.cols.includes(key) &&
                        !items.value.rows.includes(key) &&
                        key !== 'value'
                    )
                removeFilterKeys.forEach(key => delete valueFilter[key])
                const filter = {
                    cols: items.value.cols,
                    rows: items.value.rows,
                    valueFilter
                }
                setFilter(filter)
            }
        }
        const namespace = process.env.VUE_APP_NAMESPACE
        const locale = computed(() => vm.$t('org.locale'))
        const attributeMap = computed(() => {
            return attributeItems.value.reduce((accumulator, currentValue) => {
                accumulator[currentValue.key] = currentValue
                return accumulator
            }, {})
        })
        const pivotData = computed(() => {
            const propsOptions = {
                locales: locales,
                data: data.value,
                aggregators: aggregators.value,
                aggregatorName: aggregatorName,
                rendererName: rendererName.value,
                renderers: renderers.value,
                valueFilter: savedFilter.value,
                rows: items.value.rows,
                cols: items.value.cols,
                vals: vals.value,
                hiddenAttributes: hiddenAttributes.value,
                disabledFromDragDrop: disabledFromDragDrop.value,
                defaultAttributes: defaultAttributes.value,
                menuLimit: 15000
            }
            const PivotData = PivotUtilities.PivotData
            return new PivotData(propsOptions)
        })
        const csvDownload = () => {
            const items = pivotData.value.props.cols.concat(pivotData.value.props.rows)
            // const attrs = attributeItems.value.filter(item => items.includes(item.key)).map(item => item.key).concat([costPivotValue.value])
            const attrs = items.concat([outputFormatting.selected])
            if (attrs.indexOf('usageAccountId') > -1) {
                attrs.splice(attrs.indexOf('usageAccountId'), 0, 'projectName')
            }
            const list = pivotData.value.getFilteredData()
            let tData = [attrs]
            const results = list.map(item => {
                const row = []
                tData[0].forEach((t) => {
                    if (t === outputFormatting.selected) {
                        row.push(item.value)
                    } else if (t === 'projectName') {
                        const endIndex = item.usageAccountId.lastIndexOf(')')
                        const usageAccountId = item.usageAccountId.substring(endIndex - 12, endIndex)
                        row.push(accountMap.value[usageAccountId])
                    } else if (t === 'usageAccountId') {
                        const endIndex = item.usageAccountId.lastIndexOf(')')
                        const usageAccountId = item.usageAccountId.substring(endIndex - 12, endIndex)
                        row.push(usageAccountId)
                    } else {
                        row.push(item[t])
                    }
                })
                return row
            })
            tData = tData.concat(results)
            const tmp = tData.map(data => `"${data.join('","')}"`)
            const csv = [tmp.join('\n')]
            const csvFile = new Blob(['\ufeff', csv], { type: 'text/csv' })
            const url = window.URL.createObjectURL(csvFile)
            const downloadLink = document.createElement('a')
            downloadLink.download = 'billing.exported.csv'
            downloadLink.href = url
            downloadLink.click()
            window.URL.revokeObjectURL(url)
        }

        const pivotViewTable = () => {
            viewTable.value = !viewTable.value
        }
        const largeFileDownload = (dType) => {
            currentDType.value = dType
            context.emit('download:largeFile', { dType })
        }
        const handleClickDownload = () => {
            if (downloadLink.value) {
                window.location.href = downloadLink.value
            }
        }

        // data 응답 갱신 → dataValues 누적 push (add-only).
        // 사이드바 above(regular) 는 dataValues + 현재 응답에 있는 filter 값.
        // 필터 적용으로 응답이 줄어도 dataValues 는 add-only 라 과거 본 값들이 italic 으로 가지 않는다.
        watch(() => props.data, accumulateFromData, { deep: true, immediate: true })

        // 외부에서 pivotFilter 가 들어오는 경로(저장된 필터 적용 / applyFilterValues 의 emit) 에서
        // 모든 차원을 filterAccumValues 에 add-only push. ALWAYS_LATEST_KEYS 도 동일 — 컨텍스트
        // 전환 시점의 일괄 클리어는 accumulateFromData 의 단일 리셋 게이트가 담당.
        // 이 풀과 현재 응답(uniqValues)의 차집합이 italic 영역 (stale filter).
        watch(() => props.pivotFilter, (newFilter) => {
            if (!newFilter) return
            Object.keys(newFilter).forEach(key => {
                if (!filtering.filterAccumValues[key]) {
                    filtering.filterAccumValues[key] = []
                }
                newFilter[key].forEach(val => {
                    if (!filtering.filterAccumValues[key].includes(val)) {
                        filtering.filterAccumValues[key].push(val)
                    }
                })
            })
        }, { deep: true, immediate: true })

        watch(() => props.downloadMode, (newVal, oldVal) => {
            if (!newVal && oldVal) {
                currentDType.value = null
            }
            // 모드 전환 시 사이드바 UI 상태만 초기화. dataValues / filterAccumValues 누적은 보존 (새로고침이 유일한 리셋 트리거).
            filtering.valuesOfCurrentFilter = []
            filtering.selectedFilter = null
            filtering.filterString = ''
            filtering.filterStringTemp = ''
        })

        watch(() => props.items, () => {
            config.value.rows = props.items.rows
            config.value.cols = props.items.cols
        }, {
            deep: true
        })
        const sources = computed(() => ({ rows: config.value.rows, cols: config.value.cols }))
        watch(sources, (value, oldValue) => {
            const newCompositSources = [...value.rows, ...value.cols].sort()
            if (JSON.stringify(newCompositSources) !== JSON.stringify(compositSources.value)) {
                const newItems = Object.assign({}, {
                    ...items.value,
                    rows: value.rows,
                    cols: value.cols
                })
                context.emit('sync:items', newItems)
                context.emit('sync:compositSource', value)

                // 다운로드 모드에서 필드 변경 시 currentDType 초기화
                if (downloadMode.value && currentDType.value === 'file') {
                    currentDType.value = null
                }
            }
        }, {
            immediate: false,
            deep: true
        })
        watch(() => outputFormatting.selected, (value, oldValue) => {
            const newItems = Object.assign({}, {
                ...items.value,
                value
            })
            context.emit('sync:items', newItems)
        })

        return {
            skin,
            pivotValues,
            locales,
            aggregators,
            aggregatorName,
            rendererName,
            renderers,
            savedFilter,
            value,
            vals,
            hiddenAttributes,
            disabledFromDragDrop,
            defaultAttributes,
            descriptionItemsAttributes,
            resourceTagsAttributes,
            attributeOfDescriptionType,
            attributeOfResourceType,
            namespace,
            locale,
            attributeMap,
            viewTable,
            config,
            rows,
            cols,
            currentDType,
            updateValueFilter,
            handleClickDownload,
            csvDownload,
            pivotViewTable,
            largeFileDownload,
            checkRemove,
            rendering,
            outputFormatting,
            filtering,
            getAttributionName,
            getAttributionFilterValueLength,
            applyFilterForFiltering,
            openFilterModal,
            selectAllFilterValues,
            applyFilterValues,
            costPivotStatusCode,
            scrollerRef
        }
    }
}
</script>

<style lang="scss">
@import "./pivottable.scss";

.cost-pivot-tools-disabled {
    opacity: 0.5;
}

// b-sidebar-body 의 자체 overflow 를 무력화하고 flex 컨테이너로 전환.
// RecycleScroller 가 자기 스크롤을 갖도록 하기 위함 (이중 스크롤 방지).
.b-sidebar > .b-sidebar-body.cost-pivot-sidebar-body {
    overflow: hidden;
    display: flex;
    flex-direction: column;

    > .sticky-top {
        flex-shrink: 0;
    }

    > .cost-pivot-filter-scroller {
        flex: 1 1 auto;
        min-height: 0;
    }
}

// RecycleScroller 의 :item-size="32" 와 정합. 1행 고정 + 긴 값 truncate.
.cost-pivot-filter-row {
    height: 32px;
    line-height: 1;
    align-items: center;
    overflow: hidden;

    .cost-pivot-filter-checkbox {
        min-width: 0;
        width: 100%;

        .custom-control-label {
            width: 100%;
            min-width: 0;
            display: flex;
            align-items: center;

            > span {
                display: inline-block;
                max-width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }
    }
}

// regular ↔ italic 영역 구분선. RecycleScroller item-size=32 와 정합.
.cost-pivot-filter-separator {
    height: 32px;
    display: flex;
    align-items: center;
    padding: 0 0.25rem;

    > hr {
        width: 100%;
    }
}
</style>
